import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { helpCategories, helpArticles, getArticlesByCategory, searchArticles } from '../data/helpArticles';
import { Search, ChevronRight, ArrowLeft } from 'lucide-react';
export function Help() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const selectedCategory = searchParams.get('category') || '';
  const filteredArticles = useMemo(() => {
    if (searchQuery.trim()) {
      return searchArticles(searchQuery);
    }
    if (selectedCategory) {
      return getArticlesByCategory(selectedCategory);
    }
    return [];
  }, [searchQuery, selectedCategory]);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({
        q: searchQuery
      });
    } else {
      setSearchParams({});
    }
  };
  const handleCategoryClick = (categorySlug: string) => {
    setSearchQuery('');
    setSearchParams({
      category: categorySlug
    });
  };
  const clearFilters = () => {
    setSearchQuery('');
    setSearchParams({});
  };
  const showResults = searchQuery.trim() || selectedCategory;
  const currentCategory = helpCategories.find(c => c.slug === selectedCategory);
  return <Layout data-id="element-402">
      <div className="min-h-screen bg-gray-50" data-id="element-403">
        {/* Hero Section */}
        <div className="bg-white border-b" data-id="element-404">
          <div className="container mx-auto px-4 py-16 text-center" data-id="element-405">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-406">Help Center</h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto" data-id="element-407">
              Got questions? We've got answers. Search or browse to find what
              you need.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto" data-id="element-408">
              <div className="relative" data-id="element-409">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-410" />
                <input type="text" placeholder="Search for help..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 text-lg border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent" data-id="element-411" />
              </div>
            </form>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12" data-id="element-412">
          {showResults /* Search/Category Results */ ? <div data-id="element-413">
              <button onClick={clearFilters} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors" data-id="element-414">
                <ArrowLeft className="w-4 h-4" data-id="element-415" />
                Back to all categories
              </button>

              <h2 className="text-2xl font-bold mb-2" data-id="element-416">
                {searchQuery ? `Search results for "${searchQuery}"` : currentCategory?.name}
              </h2>
              <p className="text-muted-foreground mb-8" data-id="element-417">
                {filteredArticles.length} article
                {filteredArticles.length !== 1 ? 's' : ''} found
              </p>

              {filteredArticles.length > 0 ? <div className="grid gap-4" data-id="element-418">
                  {filteredArticles.map(article => <Link key={article.id} to={`/help/${article.slug}`} className="bg-white p-6 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group" data-id="element-419">
                      <div className="flex items-start justify-between gap-4" data-id="element-420">
                        <div data-id="element-421">
                          <span className="text-xs font-medium text-pink-500 uppercase tracking-wider" data-id="element-422">
                            {article.category}
                          </span>
                          <h3 className="text-lg font-bold mt-1 group-hover:text-pink-500 transition-colors" data-id="element-423">
                            {article.title}
                          </h3>
                          <p className="text-muted-foreground mt-2 line-clamp-2" data-id="element-424">
                            {article.metaDescription}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors flex-shrink-0 mt-1" data-id="element-425" />
                      </div>
                    </Link>)}
                </div> : <div className="text-center py-12" data-id="element-426">
                  <p className="text-xl text-muted-foreground mb-4" data-id="element-427">
                    No articles found
                  </p>
                  <p className="text-muted-foreground" data-id="element-428">
                    Try a different search term or browse categories below
                  </p>
                </div>}
            </div> /* Category Grid */ : <div data-id="element-429">
              <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-430">
                Browse by Category
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-431">
                {helpCategories.map(category => {
              const articleCount = getArticlesByCategory(category.slug).length;
              return <button key={category.slug} onClick={() => handleCategoryClick(category.slug)} className="bg-white p-8 rounded-2xl border border-gray-100 hover:border-pink-200 hover:shadow-lg transition-all text-left group" data-id="element-432">
                      <div className="text-4xl mb-4" data-id="element-433">{category.icon}</div>
                      <h3 className="text-xl font-bold mb-2 group-hover:text-pink-500 transition-colors" data-id="element-434">
                        {category.name}
                      </h3>
                      <p className="text-muted-foreground mb-4" data-id="element-435">
                        {category.description}
                      </p>
                      <span className="text-sm text-pink-500 font-medium" data-id="element-436">
                        {articleCount} article{articleCount !== 1 ? 's' : ''} →
                      </span>
                    </button>;
            })}
              </div>
            </div>}

          {/* Popular Articles */}
          {!showResults && <div className="mt-16" data-id="element-437">
              <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-438">
                Popular Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-439">
                {['what-is-fling', 'install', 'create-fling', 'flinglink-plus', 'daily-sparks', 'faq'].map(slug => {
              const article = helpArticles.find(a => a.slug === slug);
              if (!article) return null;
              return <Link key={article.id} to={`/help/${article.slug}`} className="bg-white p-5 rounded-xl border border-gray-100 hover:border-pink-200 hover:shadow-md transition-all group flex items-center gap-4" data-id="element-440">
                      <div className="flex-1" data-id="element-441">
                        <h3 className="font-bold group-hover:text-pink-500 transition-colors" data-id="element-442">
                          {article.title}
                        </h3>
                        <span className="text-sm text-muted-foreground" data-id="element-443">
                          {article.category}
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors" data-id="element-444" />
                    </Link>;
            })}
              </div>
            </div>}

          {/* Contact Support */}
          <div className="mt-16 text-center" data-id="element-445">
            <div className="bg-white rounded-2xl p-8 max-w-2xl mx-auto border border-gray-100" data-id="element-446">
              <h3 className="text-xl font-bold mb-2" data-id="element-447">Still need help?</h3>
              <p className="text-muted-foreground mb-4" data-id="element-448">
                Can't find what you're looking for? Our support team is here for
                you.
              </p>
              <a href="mailto:help@flinglink.app" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white transition-transform hover:scale-105" style={{
              backgroundColor: '#ff6b6b'
            }} data-id="element-449">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
}